import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "../../blent-component/Button";
import { TextInput } from "../../blent-component/TextInput";
import { Toggle } from "../../blent-component/Toggle";
import { Icon } from "../../blent-component/Icon";
import { Select } from "../../blent-component/Select";
import { ManualAutoSelect } from "./ManualAutoSelect";

import {
  JobPropertiesSetterProps,
  GradientSetterProps,
  GradientType,
} from "./../../types/globals.types";
import { SLOGANS } from "./../../utils/lists";

import {
  getWorksetups,
  getSeniorities,
  getLocations,
} from "./../../api/global";
import { SelectItemProps } from "../../types/globals.types";
import styles from "./styles.module.scss";

export const PropertiesSetter: React.FC<
  JobPropertiesSetterProps & GradientSetterProps
> = ({
  jobFunctionFromList,
  jobFunctionManually,
  setJobFunctionFromList,
  setJobFunctionManually,

  //
  jobTitle,
  setJobTitle,
  //
  jobRegion,
  setJobRegion,

  //Seniorty
  jobSeniorityFromList,
  jobSeniorityManually,
  setJobSeniorityFromList,
  setJobSeniorityManually,

  //Work setup
  jobWorkSetUpFromList,
  jobWorkSetUpManually,
  setJobWorkSetUpFromList,
  setJobWorkSetUpManually,

  //Locations
  jobLocationFromList,
  setJobLocationFromList,
  jobLocationManually,
  setJobLocationManually,
  //
  jobSummary,
  setJobSummary,
  //
  slogan,
  setSlogan,
  //
  gradient,
  setGradient,
}) => {
  const [functions, setFunctions] = useState<SelectItemProps[]>([]);
  const [worksetups, setWorksetups] = useState<SelectItemProps[]>([]);
  const [seniorities, setSeniorities] = useState<SelectItemProps[]>([]);
  const [locations, setLocations] = useState<SelectItemProps[]>([]);
  const [isManualFunction, setIsManualFunction] = useState<boolean>(false);
  const [isManualLocation, setIsManualLocation] = useState<boolean>(false);
  const [isManualSeniority, setIsManualSeniority] = useState<boolean>(false);
  const [isManualWorkSetUp, setIsManualWorkSetUp] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const handleFunctionIsManual = (v: boolean) => {
    setJobFunctionFromList([]);
    setJobFunctionManually("");
    setIsManualFunction(v);
  };

  const handleLocationIsManual = (v: boolean) => {
    setJobLocationFromList([]);
    setJobLocationManually("");
    setIsManualLocation(v);
  };
  const handleWorkSetUpIsManual = (v: boolean) => {
    setJobWorkSetUpFromList([]);
    setJobWorkSetUpManually("");
    setIsManualWorkSetUp(v);
  };
  const handleSeniorityIsManual = (v: boolean) => {
    setJobSeniorityFromList([]);
    setJobSeniorityManually("");
    setIsManualSeniority(v);
  };

  useEffect(() => {
    const setListsFromLanguageFiles = async () => {
      const loadSelectContent = async (
        namespace: string
      ): Promise<SelectItemProps[]> => {
        const language = i18n.language || "en"; // Fallback to 'en' if i18n.language is not set

        let translations: { [key: string]: string } = {};
        try {
          const module = await import(
            `../../locales/${language}/${namespace}.json`
          );
          translations = module.default;
        } catch (error) {
          console.error(
            "Error loading select content in ",
            language,
            ":",
            error
          );
          // Fallback to English if the current language file is not found
          const module = await import(`../../locales/en/${namespace}.json`);
          translations = module.default;
        }
        const result = Object.keys(translations).map((key) => ({
          value: key,
          label: translations[key],
        }));
        return result;
      };

      const _functions = await loadSelectContent("function");
      const _workSetups = await loadSelectContent("workSetUp");
      const _locations = await loadSelectContent("location");
      const _seniorities = await loadSelectContent("seniority");

      const _ordredLoctions = _locations.sort((a, b) =>
        a.label.localeCompare(b.label)
      ); // Sort by `label`

      setFunctions(_functions);
      setWorksetups(_workSetups);
      setSeniorities(_seniorities);
      setLocations(_ordredLoctions);
    };
    try {
      setListsFromLanguageFiles();
      // setListsFromServer();
    } catch (error) {
      throw error;
    }
  }, [i18n.language, t]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logo}>
          <Icon name="LogoBat" size={72} />
          {/* <img src="/logo/logo_bat_black_v1_0.png" alt="Bacardi" /> */}
        </div>
        <div className={styles.toolName}>LinkedIn Vacancy Ads</div>
      </div>

      {isManualFunction && (
        <TextInput
          size="xl"
          label={t("functionLabel")}
          value={jobFunctionManually}
          onChange={(v: string) => setJobFunctionManually(v)}
          rightSideNode={
            <IconButton
              icon={<Icon name="PencilSlash" size={16} color="#888" />}
              size="s"
              onClick={() => handleFunctionIsManual(false)}
              variant="tertiary"
              corners="rounded"
              noBorder
            />
          }
        />
      )}
      {!isManualFunction && (
        <ManualAutoSelect
          label={t("functionLabel")}
          size="xl"
          options={functions}
          selected={jobFunctionFromList}
          onSelect={(v) => setJobFunctionFromList(v)}
          changeToManual={() => handleFunctionIsManual(true)}
        />
      )}

      <TextInput
        size="xl"
        label={t("titleLabel")}
        value={jobTitle}
        // placeholder="Add the title..."
        onChange={(v) => setJobTitle(v)}
      />
      <TextInput
        size="xl"
        label={t("regionLabel")}
        value={jobRegion}
        onChange={(v) => setJobRegion(v)}
      />

      {isManualLocation && (
        <TextInput
          size="xl"
          label={t("locationLabel")}
          value={jobLocationManually}
          // placeholder="Add the location..."
          onChange={(v: string) => setJobLocationManually(v)}
          rightSideNode={
            <IconButton
              icon={<Icon name="PencilSlash" size={16} color="#888" />}
              size="s"
              onClick={() => handleLocationIsManual(false)}
              variant="tertiary"
              corners="rounded"
              noBorder
            />
          }
        />
      )}
      {!isManualLocation && jobLocationFromList && (
        <ManualAutoSelect
          label={t("locationLabel")}
          size="xl"
          autosuggest
          options={locations}
          selected={jobLocationFromList}
          onSelect={(v) => setJobLocationFromList(v)}
          changeToManual={() => handleLocationIsManual(true)}
        />
      )}

      {isManualWorkSetUp && (
        <TextInput
          size="xl"
          label={t("workSetupLabel")}
          value={jobWorkSetUpManually}
          onChange={(v: string) => setJobWorkSetUpManually(v)}
          rightSideNode={
            <IconButton
              icon={<Icon name="PencilSlash" size={16} color="#888" />}
              size="s"
              onClick={() => handleWorkSetUpIsManual(false)}
              variant="tertiary"
              corners="rounded"
              noBorder
            />
          }
        />
      )}
      {!isManualWorkSetUp && (
        <ManualAutoSelect
          label={t("workSetupLabel")}
          size="xl"
          options={worksetups}
          selected={jobWorkSetUpFromList}
          onSelect={(v) => setJobWorkSetUpFromList(v)}
          changeToManual={() => handleWorkSetUpIsManual(true)}
        />
      )}

      {isManualSeniority && (
        <TextInput
          size="xl"
          label={t("seniorityLabel")}
          value={jobSeniorityManually}
          onChange={(v: string) => setJobSeniorityManually(v)}
          rightSideNode={
            <IconButton
              icon={<Icon name="PencilSlash" size={16} color="#888" />}
              size="s"
              onClick={() => handleSeniorityIsManual(false)}
              variant="tertiary"
              corners="rounded"
              noBorder
            />
          }
        />
      )}
      {!isManualSeniority && (
        <ManualAutoSelect
          label={t("seniorityLabel")}
          size="xl"
          options={seniorities}
          selected={jobSeniorityFromList}
          onSelect={(v) => setJobSeniorityFromList(v)}
          changeToManual={() => handleSeniorityIsManual(true)}
        />
      )}

      <Select
        label={t("slogan")}
        // placeholder="Select category..."
        size="xl"
        options={SLOGANS}
        selected={slogan}
        onSelect={setSlogan}
      />

      <div className={styles.section}>
        <div className={styles.sectionLabel}>{t("gradientLabel")}</div>
        <Toggle
          auto
          onChange={(v) => {
            setGradient(v as GradientType);
          }}
          options={[
            {
              label: `${t("pink")}`,
              value: "pink",
            },
            {
              label: `${t("blue")}`,
              value: "blue",
            },
            {
              label: `${t("green")}`,
              value: "green",
            },
            {
              label: `${t("purple")}`,
              value: "purple",
            },
            {
              label: `${t("yellow")}`,
              value: "yellow",
            },
          ]}
          required
          value={gradient}
        />
      </div>
    </div>
  );
};
